/* global DEBUG_LOG, API_ORIGIN_OVERRIDE */

// mixpanel-specific report utils

import queryString from 'query-string';

import {capitalize, htmlEncodeString, pluralize} from '../../util';
import {COUNTRIES, NOT_APPLICABLE_PROPERTY_VALUE} from '../constants';
import {getEventDisplayName, getPropertyDisplayName} from '../display-name-registry';

/**
 * utils for standalone vs hosted usage
 */

export function isStandalone() {
  return typeof mp === `undefined`;
}

export function apiHost() {
  const envApiOrigin = typeof API_ORIGIN_OVERRIDE !== `undefined` ? API_ORIGIN_OVERRIDE : null;
  if (envApiOrigin) {
    return envApiOrigin;
  }

  if (isStandalone()) {
    // if running standalone on devbox or staging (note: Dashboard app can run standalone in staging), don't override with mixpanel.com
    const devOrigin = window.location.origin.match(/dev(box)?\.mixpanel\.org/);
    const stagingOrigin = window.location.origin.match(/staging-[\d+]\.mixpanel.com/);
    const urlOptions = queryString.parse(window.location.search);
    return (
      urlOptions[`api_origin`] ||
      ((devOrigin || stagingOrigin) ? `` : `https://mixpanel.com`)
    );
  }

  return ``;
}

export function getCachedStandaloneAccessToken() {
  if (isStandalone() && typeof localStorage !== `undefined`) {
    const ttlMs = 24 * 60 * 60 * 1000; // one day expiry
    const accessToken = localStorage.getItem(`access_token`);
    const timestamp = localStorage.getItem(`access_token_timestamp`);

    if (accessToken && timestamp && parseInt(timestamp, 10) > (Date.now() - ttlMs)) {
      return accessToken;
    }
  }

  return null;
}


export function updateStandaloneAccessToken(accessToken) {
  if (isStandalone() && typeof localStorage !== `undefined`) {
    localStorage.setItem(`access_token`, accessToken || ``);
    localStorage.setItem(`access_token_timestamp`, Date.now());
  }
}

/**
 * fetch current project ID, either from:
 * - mp.report.globals (when in hosted context)
 * - URL query string (when standalone)
 */
export function getProjectID() {
  if (isStandalone()) {
    return queryString.parse(window.location.search).project_id;
  } else {
    return window.mp.report && window.mp.report.globals.project_id;
  }
}

/**
 * Defensively access mp.report.globals
 */
export function getMpReportGlobals() {
  // eslint-disable-next-line no-undef
  return (typeof mp === `object` && mp.report && mp.report.globals) || {};
}

/*
 * downloadData()
 *
 * Initiates a direct download to the browser for any HTTP method,
 * using a terrible hack of submitting a hidden form to a hidden
 * iframe target.
 *
 */
let downloadIdCounter = 1;
export function downloadData(endpoint, params, method=`POST`) {
  // prepare target iframe
  const exportFrameID = `download-frame-${downloadIdCounter++}`;
  const exportFrame = document.createElement(`iframe`);
  exportFrame.id = exportFrameID;
  exportFrame.name = exportFrameID;
  exportFrame.src = ``;
  exportFrame.style.display = `none`;
  document.body.appendChild(exportFrame);

  // prepare source form
  const postForm = document.createElement(`form`);
  postForm.action = endpoint;
  postForm.method = method;
  postForm.style.display = `none`;
  postForm.target = exportFrameID;
  postForm.innerHTML = Object.keys(params)
    .map(p => `<input type="hidden" name="${p}" value="${htmlEncodeString(params[p])}"/>`)
    .join(``);

  // submit request
  document.body.appendChild(postForm);
  postForm.submit();
}

export function downloadLocalCSVData(filename, dataStr) {
  // launch named download via hidden link
  const link = document.createElement(`a`);

  const downloadAttrSupported = `download` in link;
  if (downloadAttrSupported) {
    // prepare blob
    const blob = new Blob([dataStr], {type: `text/csv;charset=utf-8`});
    const blobURL = URL.createObjectURL(blob);

    // launch named download via hidden link
    link.style.display = `none`;
    link.href = blobURL;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // clean up
    URL.revokeObjectURL(blobURL);
    link.remove();
  } else {
    // Work-around for Safari. One drawback of this method is that it doesn't support setting a filename.
    // On Safari, it downloads the file with the name 'Unknown'
    window.open(`data:attachment/csv;charset=utf-8,` + encodeURIComponent(dataStr));
  }
}

// TODO epurcer - replace this with a more general-purpose tool like https://www.npmjs.com/package/debug
function getLogger(level) {
  if (typeof DEBUG_LOG !== `undefined` && DEBUG_LOG === `true`) {
    /* eslint-disable no-console */
    return function() { console[level](...arguments); };
    /* eslint-enable no-console */
  } else {
    return function() {};
  }
}

/**
 * @type {{[level: string]: function}}
 */
export const debug = [`log`, `info`, `warn`, `error`, `critical`].reduce((ret, level) =>
  ({...ret, [level]: getLogger(level)}), {});

export function getIconForEvent(mpEvent) {
  if (mpEvent.is_collect_everything_event) {
    return `autotrack`;
  } else if (mpEvent.custom) {
    return `custom-events`;
  } else {
    return `event`;
  }
}

export function renameEvent(event) {
  return getEventDisplayName(event);
}

const PROPERTY_TYPE_ICON_MAP = {
  boolean: `type-boolean`,
  datetime: `type-date`,
  list: `type-list`,
  number: `type-number`,
  object: `type-object`,
  string: `type-text`,
};

const PROPERTY_TYPE_LABEL_MAP = {
  boolean: `True / False`,
  datetime: `Date`,
  list: `List`,
  number: `Number`,
  object: `Object`,
  string: `String`,
};

export function getIconForPropertyType(type) {
  return PROPERTY_TYPE_ICON_MAP[type];
}

export function getLabelForPropertyType(type) {
  return PROPERTY_TYPE_LABEL_MAP[type];
}

export function renameProperty(property, {dataGroupId=null}={}) {
  const renamedProperty = getPropertyDisplayName(property, {dataGroupId});
  if (renamedProperty !== property) {
    return renamedProperty;
  }

  // default conversion for all properties starting with '$'
  if (property && property.length > 1 && property[0] === `$`) {
    return property
      .slice(1)
      .split(`_`)
      .map(capitalize)
      .join(` `);
  }

  return property;
}

export function renamePropertyType(type) {
  return {
    number: `Integer`,
    datetime: `Date`,
    boolean: `True/False`,
  }[type] || capitalize(type);
}

export function renameCountryCode(value) {
  return COUNTRIES[String(value).toUpperCase()] || value;
}

export function isBrowserEnvironment() {
  return typeof window !== `undefined`;
}

/**
 * Provides explicit configuration of global mapping state for the renamePropertyValue function. This allows
 * callers of renamePropertyValue to retrieve mapped properties values without access to the configurations.
 */
export function updatePropertyValueMappings(propertyValueMappings) {
  if (!isBrowserEnvironment()) {
    return;
  }
  // TODO(RNA-230): Remove dependency on mappings being global by refactoring callsites to pass the mappings to
  // renamePropertyValue.
  window.__propertyValueMappings = propertyValueMappings;
}

/**
 * Provides the list of global mappings
 */
export function getPropertyValueMappings() {
  if (!isBrowserEnvironment()) {
    return null;
  }
  return window.__propertyValueMappings;
}

export function renamePropertyValue(value, propertyName) {
  if (propertyName === `$event`) {
    // Value is an event
    return renameEvent(value);
  } else if (propertyName === `$people`) {
    // Value is a property
    return renameProperty(value); // value is $all_people
  }

  // Value is a property value

  if (value === NOT_APPLICABLE_PROPERTY_VALUE) {
    return `Not Applicable`;
  } else if (/country_code/.test(propertyName)) {
    return renameCountryCode(value);
  }

  const id = parseInt(value, 10);
  const propertyValueMappings = getPropertyValueMappings();
  if (!propertyValueMappings || Number.isNaN(id)) {
    return value;
  }

  let mappedPropertyValue = value;
  const {projectCampaigns={}, campaignVariants=[], experiments=[]} = propertyValueMappings;
  switch (propertyName) {
    case `$campaigns`:
    case `campaign_id`:
      // map message IDs to their names
      mappedPropertyValue = projectCampaigns[id] || value;
      break;
    case `message_id`: {
      // map message variant IDs to their numbers and campaign
      const campaign = campaignVariants.find(campaign => campaign.variants.includes(id));
      if (campaign) {
        const variantIdx = campaign.variants.indexOf(id);
        mappedPropertyValue = variantIdx === 0 ? `${campaign.name} - Original variant` : `${campaign.name} - Variant ${variantIdx}`;
      }
      break;
    }
    case `$experiment_id`: {
      // map experiment IDs to their names
      const experiment = experiments.find(exp => exp.id === id);
      if (experiment) {
        mappedPropertyValue = experiment.name;
      }
      break;
    }
    case `$variant_id`: {
      // map experiment variant IDs to their numbers and experiment
      const experiment = experiments.find(exp => exp.variants.some(variant => variant.id === id));
      if (experiment) {
        const variantIdx = experiment.variants
          .map(variant => variant.id)
          .sort((a, b) => a - b)
          .indexOf(id);
        mappedPropertyValue = variantIdx === 0 ? `${experiment.name} - Original variant` : `${experiment.name} - Variant ${variantIdx}`;
      }
      break;
    }
    default:
      break;
  }

  return mappedPropertyValue;
}

export function formatMaxHistoryDays(maxHistoryDays) {
  if (maxHistoryDays < 365) {
    return `${maxHistoryDays} days`;
  } else {
    const years = Math.floor(maxHistoryDays / 365);
    return `${years} ${pluralize(`year`, years)}`;
  }
}


export function whiteoutReport() {
  const whiteoutLoaderElem = document.querySelector(`.whiteout_loader`);
  if (whiteoutLoaderElem) {
    whiteoutLoaderElem.classList.remove(`hidden`);
  }
}
